import React, { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cx } from '../../lib/cx';

const VARIANTS = {
  primary: 'bg-accent-solid text-on-accent shadow-sm hover:bg-accent-solid/90',
  secondary: 'border border-line-strong bg-sunken text-fg-2 hover:bg-raised hover:text-fg',
  accent: 'border border-accent/30 bg-accent/10 text-accent-fg hover:bg-accent/20',
  ghost: 'text-muted hover:bg-raised hover:text-fg',
  danger: 'border border-danger/30 bg-danger/10 text-danger hover:bg-danger/20',
};

const SIZES = {
  sm: 'h-8 gap-1.5 px-2.5 text-xs',
  md: 'h-9 gap-2 px-3.5 text-sm',
  lg: 'h-11 gap-2 px-5 text-sm',
};

const ICON_SIZES = { sm: 14, md: 16, lg: 16 };

export const Button = forwardRef(function Button(
  { variant = 'secondary', size = 'md', icon: Icon, loading = false, className, children, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg font-medium transition-colors',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading
        ? <Loader2 size={ICON_SIZES[size]} className="animate-spin" aria-hidden="true" />
        : Icon && <Icon size={ICON_SIZES[size]} aria-hidden="true" />}
      {children}
    </button>
  );
});

/** Square icon-only button. `label` is required: it is the accessible name and tooltip. */
export const IconButton = forwardRef(function IconButton(
  { icon: Icon, label, variant = 'ghost', size = 'md', className, iconSize, ...props },
  ref,
) {
  const dims = { sm: 'h-8 w-8', md: 'h-9 w-9', lg: 'h-11 w-11' }[size];
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-lg transition-colors',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        dims,
        className,
      )}
      {...props}
    >
      <Icon size={iconSize || ICON_SIZES[size]} aria-hidden="true" />
    </button>
  );
});
